/**
 * `growth/contextual-provider` — backend web **no personalizado**.
 *
 * ⚠️ **[DIVERGENCE FROM THE CANON — back-port candidate]**. The canon
 * (`listto`) has `none`, `adsense`, and `fake`; kid-games contributed `admob`. This is
 * what bso contributes, and it is not a preference of this app: it is the only way to
 * make «ads contextuales, NUNCA conductuales» a **technical** property.
 *
 * ── The hole this closes ────────────────────────────────────────────────────
 *
 * `WebAdProvider` requests only non-personalized ads when the config is
 * **child-directed** (`tag_for_child_directed_treatment`). That is correct for the
 * child regime and **does not work for bso**: bso's users are adults, and
 * marking the app as directed at children to obtain non-personalization as a
 * side effect would lie to the network — with the Families obligations that
 * entails in Play, and without covering the real case.
 *
 * What bso needs is what AdSense exposes at the PAGE level:
 *
 * ```js
 * (adsbygoogle = window.adsbygoogle || []).requestNonPersonalizedAds = 1;
 * (adsbygoogle = window.adsbygoogle || []).restrictDataProcessing = 1;
 * ```
 *
 * ── Why ORDER is the detail that matters ────────────────────────────────
 *
 * The two flags are read when the **first** ad request is served.
 * If they are set after injecting the `<script>`, there is a window in which an
 * ad may be served personalized — and that window is precisely the first
 * load, on a screen with religious content. Therefore this provider
 * **seeds the flags BEFORE delegating `init()`**, and a test verifies
 * this by comparing effect order, not just the final state.
 *
 * `scope` is injectable so it can be tested without DOM or network.
 */

import { AD_NETWORK_NONE } from "./config";
import { createDomScriptLoader, makeAdProvider, WebAdProvider } from "./provider";
import type { AdScriptLoader } from "./provider";
import type {
  AdEvent,
  AdEventName,
  AdInitOptions,
  AdKind,
  AdProvider,
  AdsConfig,
  AdShowResult,
} from "./types";

/** Bandera de AdSense: "no me sirvas anuncios personalizados". */
export const NON_PERSONALIZED_FLAG = "requestNonPersonalizedAds";
/** AdSense flag for the restricted regime (CCPA/CPRA). */
export const RESTRICT_DATA_PROCESSING_FLAG = "restrictDataProcessing";

/** Global AdSense queue; it is an array with properties. */
export interface AdsByGoogleQueue extends Array<unknown> {
  [NON_PERSONALIZED_FLAG]?: number;
  [RESTRICT_DATA_PROCESSING_FLAG]?: number;
}

export interface AdGlobalScope {
  adsbygoogle?: AdsByGoogleQueue;
}

/**
 * Seeds the non-personalization flags in the global queue, creating it if it
 * does not exist. Idempotent and **tighten-only**: if someone already set them, it does not lower them.
 */
export function seedNonPersonalizedAds(scope: AdGlobalScope): AdsByGoogleQueue {
  const queue: AdsByGoogleQueue = scope.adsbygoogle ?? ([] as AdsByGoogleQueue);
  queue[NON_PERSONALIZED_FLAG] = 1;
  queue[RESTRICT_DATA_PROCESSING_FLAG] = 1;
  scope.adsbygoogle = queue;
  return queue;
}

/**
 * `WebAdProvider` + guaranteed non-personalization. It delegates ALL
 * behavior (lazy load, `isEnabled`, fill, events) and only
 * interposes in `init()` to seed the flags before the SDK exists.
 */
export class ContextualWebAdProvider implements AdProvider {
  readonly id: string;
  private readonly inner: WebAdProvider;

  constructor(
    config: AdsConfig,
    loader: AdScriptLoader = createDomScriptLoader(),
    private readonly scope: AdGlobalScope = globalThis as typeof globalThis & AdGlobalScope,
  ) {
    this.inner = new WebAdProvider(config, loader);
    this.id = config.network;
  }

  init(opts: AdInitOptions = {}): Promise<void> {
    // BEFORE delegating: the SDK has not been injected yet, so the first
    // ad request is already non-personalized.
    seedNonPersonalizedAds(this.scope);
    return this.inner.init(opts);
  }

  isEnabled(kind: AdKind): boolean {
    return this.inner.isEnabled(kind);
  }

  prepare(kind: AdKind): Promise<boolean> {
    // `WebAdProvider.prepare` only looks at format: on web fill is resolved by
    // the slot resolves it when rendered, not as a per-placement preload.
    return this.inner.prepare(kind);
  }

  show(kind: AdKind, placementId: string): Promise<AdShowResult> {
    return this.inner.show(kind, placementId);
  }

  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void {
    return this.inner.on(ev, cb);
  }
}

/**
 * bso factory: identical to `makeAdProvider` except that **the real web network ALWAYS
 * goes through the contextual provider**. It is the only point in the app that
 * builds a provider, so there is no way to bypass it from a screen.
 *
 * An unknown network (or a typo in `ADS_NETWORK`) still falls back to `none`: it
 * ads are turned off; nothing is ever shipped half-configured.
 */
export function makeBsoAdProvider(
  config: AdsConfig,
  loader?: AdScriptLoader,
  scope?: AdGlobalScope,
): AdProvider {
  if (config.enabled && config.network !== AD_NETWORK_NONE && config.network === "adsense") {
    return new ContextualWebAdProvider(config, loader, scope);
  }
  return makeAdProvider(config, loader);
}
